import React, { useEffect, useRef, useState, useCallback } from 'react';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import { reverseGeocode, parseAddressComponents } from '../utils/geocodingService';
import { isCoordinateServiceable } from '../utils/serviceabilityCheck';
import { getStoreCoordinates } from '../utils/deliverySlots';
import { useMobileBack, OVERLAY_PRIORITY } from '../navigation';
import { useAppContext } from '../context/AppContext';

const LABEL_OPTIONS = ['Home', 'Work', 'Other'];

const emptyForm = {
  label: 'Home',
  line1: '',
  line2: '',
  city: '',
  pincode: '',
  location: '',
  latitude: null,
  longitude: null,
};

/**
 * AddressPicker
 * Phase 1 — MAP:  Green header + search bar, fullscreen map, fixed red pin,
 *                 "Locate me" button, white bottom card with address + Continue.
 * Phase 2 — FORM: Bottom sheet slides up for address detail review/edit + Save.
 */
function AddressPicker({ initialAddress, onSave, onClose }) {
  const mapRef       = useRef(null);
  const mapInstance  = useRef(null);
  const acService    = useRef(null);
  const sessionToken = useRef(null);
  const debounceRef  = useRef(null);
  const idleRef      = useRef(null);
  const abortRef     = useRef(false);
  const firstIdle    = useRef(true);

  const [phase, setPhase]             = useState('map');
  const [mapsReady, setMapsReady]     = useState(false);
  const [mapsError, setMapsError]     = useState('');

  // Hide bottom nav and header when AddressPicker is open
  const { setHideBottomNav } = useAppContext();
  useEffect(() => {
    setHideBottomNav(true);
    return () => setHideBottomNav(false);
  }, [setHideBottomNav]);

  // Get store coordinates from Firestore settings (dynamic)
  const storeCoords = getStoreCoordinates();
  const defaultCenter = (storeCoords.latitude && storeCoords.longitude)
    ? { lat: storeCoords.latitude, lng: storeCoords.longitude }
    : { lat: 11.5064, lng: 76.4969 }; // Fallback only if settings haven't loaded

  const [searchText, setSearchText]   = useState('');

  // Handle mobile back button — close picker or go back to map phase
  const handleBackFromPicker = useCallback(() => {
    if (phase === 'form') {
      setPhase('map');
    } else {
      onClose();
    }
  }, [phase, onClose]);

  useMobileBack({
    id: 'address-picker',
    priority: OVERLAY_PRIORITY.ADDRESS_PICKER,
    isOpen: true, // Always open when rendered
    onClose: handleBackFromPicker,
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug]         = useState(false);
  const [isLocating, setIsLocating]   = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [address, setAddress]         = useState('');   // resolved address string
  const [locError, setLocError]       = useState('');
  const [centerCoords, setCenterCoords] = useState(null);

  const [form, setForm]               = useState(() =>
    initialAddress ? { ...emptyForm, ...initialAddress } : { ...emptyForm }
  );
  const [serviceability, setServiceability] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSaving, setIsSaving]       = useState(false);
  const [saveError, setSaveError]     = useState('');

  // ── Load Maps SDK ────────────────────────────────────────────────────────────
  useEffect(() => {
    loadGoogleMaps()
      .then(() => setMapsReady(true))
      .catch(e => setMapsError(e.message));
  }, []);

  // ── Reverse geocode helper ───────────────────────────────────────────────────
  const geocode = useCallback(async (lat, lng) => {
    abortRef.current = true; // Abort any previous in-flight geocode
    // Small delay to let abort propagate
    await new Promise(r => setTimeout(r, 0));
    abortRef.current = false;
    setIsGeocoding(true);
    setLocError('');
    try {
      const parsed = await reverseGeocode(lat, lng);
      if (abortRef.current) return;
      const preview = [parsed.line1, parsed.location, parsed.city]
        .filter(Boolean).join(', ');
      setAddress(preview || parsed.formattedAddress || 'Location selected');
      setForm(prev => ({
        ...prev,
        line1:    parsed.line1    || prev.line1,
        city:     parsed.city     || prev.city,
        pincode:  parsed.pincode  || prev.pincode,
        location: parsed.location || prev.location,
        latitude: lat,
        longitude: lng,
      }));
    } catch {
      if (abortRef.current) return;
      setAddress('');
      setLocError('Could not resolve address. You can still continue and fill details manually.');
      setForm(prev => ({ ...prev, latitude: lat, longitude: lng }));
    } finally {
      if (!abortRef.current) setIsGeocoding(false);
    }
  }, []);

  useEffect(() => () => { abortRef.current = true; }, []);

  // ── Init map ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapsReady || !mapRef.current || mapInstance.current) return;

    const isEditing = !!(initialAddress?.latitude && initialAddress?.longitude);
    const center = isEditing
      ? { lat: Number(initialAddress.latitude), lng: Number(initialAddress.longitude) }
      : defaultCenter;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 16,
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_CENTER,
      },
      gestureHandling: 'greedy',
      clickableIcons: false,
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
    });

    mapInstance.current = map;
    acService.current   = new window.google.maps.places.AutocompleteService();
    sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();

    idleRef.current = map.addListener('idle', () => {
      if (firstIdle.current) {
        firstIdle.current = false;
        if (isEditing) {
          const c = map.getCenter();
          setCenterCoords({ lat: c.lat(), lng: c.lng() });
          geocode(c.lat(), c.lng());
        }
        return;
      }
      const c = map.getCenter();
      const lat = c.lat(); const lng = c.lng();
      setCenterCoords({ lat, lng });

      // Debounce geocode to prevent rapid-fire calls during pan/zoom animations
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        geocode(lat, lng);
      }, 400);
    });

    if (isEditing) {
      setCenterCoords({ lat: Number(initialAddress.latitude), lng: Number(initialAddress.longitude) });
    } else {
      setTimeout(() => triggerGps(true), 300);
    }

    return () => {
      if (idleRef.current) window.google.maps.event.removeListener(idleRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsReady]);

  // ── GPS ──────────────────────────────────────────────────────────────────────
  const triggerGps = useCallback((silent = false) => {
    if (!navigator.geolocation) {
      if (!silent) setLocError('Location access is not supported on this device.');
      return;
    }
    setIsLocating(true);
    if (!silent) setLocError('');

    const attempt = (left) => {
      navigator.geolocation.getCurrentPosition(
        pos => {
          setIsLocating(false);
          const { latitude: lat, longitude: lng } = pos.coords;
          if (mapInstance.current) {
            mapInstance.current.panTo({ lat, lng });
            mapInstance.current.setZoom(17);
          }
        },
        err => {
          if (err.code === 2 && left > 0) { setTimeout(() => attempt(left - 1), 1500); return; }
          setIsLocating(false);
          if (!silent) setLocError(
            err.code === 1
              ? 'Location blocked. Enable it in your browser or phone settings.'
              : 'Unable to detect location.'
          );
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    };
    attempt(3);
  }, []);

  // ── Autocomplete ─────────────────────────────────────────────────────────────
  const handleSearchChange = e => {
    const val = e.target.value;
    setSearchText(val);
    clearTimeout(debounceRef.current);
    if (!val.trim()) { setSuggestions([]); setShowSug(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        // Use the new Places API (google.maps.places.AutocompleteSuggestion)
        // Fallback to legacy if new API not available
        if (window.google.maps.places.AutocompleteSuggestion) {
          const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: val,
            includedRegionCodes: ['in'],
            locationBias: { center: defaultCenter, radius: 50000 },
          });
          if (suggestions && suggestions.length > 0) {
            const mapped = suggestions.map(s => ({
              place_id: s.placePrediction.placeId,
              description: s.placePrediction.text.text,
              structured_formatting: {
                main_text: s.placePrediction.mainText?.text || s.placePrediction.text.text,
                secondary_text: s.placePrediction.secondaryText?.text || '',
              },
            }));
            setSuggestions(mapped);
            setShowSug(true);
          } else {
            setSuggestions([]);
            setShowSug(false);
          }
        } else if (acService.current) {
          // Legacy fallback
          acService.current.getPlacePredictions(
            { input: val, sessionToken: sessionToken.current, componentRestrictions: { country: 'in' } },
            (preds, status) => {
              if (status === 'OK' && preds) { setSuggestions(preds); setShowSug(true); }
              else { setSuggestions([]); setShowSug(false); }
            }
          );
        }
      } catch (err) {
        console.error('[Places Search]', err);
        setSuggestions([]);
        setShowSug(false);
      }
    }, 300);
  };

  const handleSelectSuggestion = pred => {
    setShowSug(false);
    setSearchText(pred.structured_formatting?.main_text || pred.description);
    setSuggestions([]);

    // Use Geocoding to get coordinates from place_id (works with both old and new API)
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: pred.place_id }, (results, status) => {
      if (status !== 'OK' || !results || !results[0]) {
        setLocError('Could not load place details. Please try again.');
        return;
      }
      const result = results[0];
      const lat = result.geometry.location.lat();
      const lng = result.geometry.location.lng();
      mapInstance.current.panTo({ lat, lng });
      mapInstance.current.setZoom(17);
      const parsed = parseAddressComponents(result);
      setAddress([parsed.line1, parsed.location, parsed.city].filter(Boolean).join(', ') || result.formatted_address || '');
      setForm(prev => ({
        ...prev,
        line1: parsed.line1 || prev.line1, city: parsed.city || prev.city,
        pincode: parsed.pincode || prev.pincode, location: parsed.location || prev.location,
        latitude: lat, longitude: lng,
      }));
      setCenterCoords({ lat, lng });
      setLocError('');
    });
  };

  // ── Continue → form phase ────────────────────────────────────────────────────
  const handleContinue = () => {
    if (!centerCoords) return;
    setServiceability(isCoordinateServiceable(centerCoords.lat, centerCoords.lng));
    setPhase('form');
  };

  // ── Form ─────────────────────────────────────────────────────────────────────
  const handleField = (f, v) => {
    setForm(prev => ({ ...prev, [f]: v }));
    if (fieldErrors[f]) setFieldErrors(prev => ({ ...prev, [f]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.label.trim())  e.label  = 'Label is required.';
    if (!form.line1.trim())  e.line1  = 'Street address is required.';
    if (!form.city.trim())   e.city   = 'City is required.';
    if (!form.pincode.trim()) e.pincode = 'Pincode is required.';
    else if (!/^\d{6}$/.test(form.pincode.trim())) e.pincode = 'Enter a valid 6-digit pincode.';
    if (form.latitude === null) e.coords = 'No location selected. Go back and pin your location.';
    return e;
  };

  const handleSave = async () => {
    setSaveError('');
    const errs = validate();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    if (!serviceability) return;
    setIsSaving(true);
    try {
      await onSave({
        label: form.label.trim(), line1: form.line1.trim(), line2: form.line2.trim(),
        city: form.city.trim(), pincode: form.pincode.trim(), location: form.location.trim(),
        latitude: Number(form.latitude), longitude: Number(form.longitude),
      });
    } catch (err) {
      setSaveError(err.message || 'Failed to save address. Please try again.');
      setIsSaving(false);
    }
  };

  const canSave = serviceability === true && form.label.trim() && form.line1.trim() &&
    form.city.trim() && /^\d{6}$/.test(form.pincode.trim()) && form.latitude !== null;

  // ── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <div style={S.root}>

      {/* ═══ MAP PHASE ═══ */}
      <div style={{ ...S.mapWrap, display: phase === 'map' ? 'flex' : 'none' }}>

        {/* Header with back + search */}
        <div style={S.header}>
          <div style={S.topBar}>
            <button style={S.backBtn} onClick={onClose} aria-label="Back">←</button>
            <span style={S.headerTitle}>Select delivery location</span>
          </div>
          <div style={S.searchRow}>
            <input
              style={S.searchInput}
              type="text"
              placeholder="Search for area, street or landmark"
              value={searchText}
              onChange={handleSearchChange}
              onFocus={() => suggestions.length > 0 && setShowSug(true)}
              autoComplete="off"
            />
            {searchText ? (
              <button style={S.searchAction} onClick={() => { setSearchText(''); setSuggestions([]); setShowSug(false); }} aria-label="Clear">✕</button>
            ) : (
              <span style={S.searchAction}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              </span>
            )}
          </div>

          {/* Autocomplete dropdown */}
          {showSug && suggestions.length > 0 && (
            <div style={S.dropdown}>
              {suggestions.map(s => (
                <button key={s.place_id} style={S.sugItem} onClick={() => handleSelectSuggestion(s)}>
                  <span style={S.sugPin}>📍</span>
                  <div style={S.sugTextWrap}>
                    <div style={S.sugMain}>{s.structured_formatting?.main_text || s.description}</div>
                    <div style={S.sugSub}>{s.structured_formatting?.secondary_text || ''}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div style={S.mapArea}>
          <div ref={mapRef} style={S.mapCanvas} />

          {/* Fixed red pin at center */}
          <div style={S.pin} aria-hidden="true">
            <svg width="36" height="48" viewBox="0 0 36 48" fill="none">
              <ellipse cx="18" cy="44" rx="6" ry="3" fill="rgba(0,0,0,0.2)"/>
              <path d="M18 0C8.06 0 0 8.06 0 18c0 12.6 18 28 18 28s18-15.4 18-28C36 8.06 27.94 0 18 0z" fill="#e53e3e"/>
              <circle cx="18" cy="17" r="7" fill="#fff"/>
              <circle cx="18" cy="17" r="3.5" fill="#e53e3e"/>
            </svg>
          </div>

          {/* Locate me button */}
          <button style={S.locateBtn} onClick={() => triggerGps(false)} disabled={isLocating}>
            {isLocating
              ? <div style={S.spinner} />
              : <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2"/></svg>
                  <span style={S.locateText}>Locate me</span>
                </>
            }
          </button>

          {/* Error banner */}
          {locError && <div style={S.locError}>{locError}</div>}

          {/* Maps error */}
          {mapsError && <div style={S.mapsErr}>⚠️ {mapsError}</div>}
        </div>

        {/* Bottom card */}
        <div style={S.bottomCard}>
          {/* Handle pill */}
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d1d5db', margin: '0 auto 16px' }} />
          <div style={S.cardTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#ef4444" stroke="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
            Selected location
          </div>
          <div style={S.cardAddress}>
            {isGeocoding || isLocating
              ? 'Finding address…'
              : address || 'Move the map to select your location'}
          </div>
          <div style={S.cardSubAddress}>
            {form.city && form.pincode ? `${form.city}, ${form.location || ''} ${form.pincode}`.trim() : ''}
          </div>
          <button
            style={{ ...S.continueBtn, ...(!centerCoords || isGeocoding || isLocating ? S.continueBtnDisabled : {}) }}
            onClick={handleContinue}
            disabled={!centerCoords || isGeocoding || isLocating}
          >
            Continue
          </button>
        </div>
      </div>

      {/* ═══ FORM PHASE ═══ */}
      {phase === 'form' && (
        <div style={S.formOverlay}>
          <div style={S.formSheet}>
            <div style={S.formHandle} />
            <div style={S.formHeader}>
              <button style={S.formBack} onClick={() => setPhase('map')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
              </button>
              <span style={S.formTitle}>Confirm Address</span>
              <div style={{ width: 32 }} />
            </div>

            {serviceability !== null && (
              <div style={serviceability ? S.svcYes : S.svcNo}>
                {serviceability ? '✓ Delivery available in your area' : '✗ Sorry, we don\'t deliver to this area yet'}
              </div>
            )}

            <div style={S.formBody}>
              {/* Label */}
              <div style={S.fg}>
                <label style={S.fl}>Save as *</label>
                <div style={S.labelRow}>
                  {LABEL_OPTIONS.map(o => (
                    <button key={o} style={{ ...S.chip, ...(form.label === o ? S.chipActive : {}) }} onClick={() => handleField('label', o)}>
                      {o === 'Home' ? '🏠' : o === 'Work' ? '💼' : '📌'} {o}
                    </button>
                  ))}
                </div>
                {fieldErrors.label && <div style={S.fe}>{fieldErrors.label}</div>}
              </div>
              {/* Line 1 */}
              <div style={S.fg}>
                <label style={S.fl}>Street / Landmark *</label>
                <input style={{ ...S.inp, ...(fieldErrors.line1 ? S.inpErr : {}) }} placeholder="House no., street, landmark" value={form.line1} onChange={e => handleField('line1', e.target.value)} />
                {fieldErrors.line1 && <div style={S.fe}>{fieldErrors.line1}</div>}
              </div>
              {/* Line 2 */}
              <div style={S.fg}>
                <label style={S.fl}>Apartment / Floor (optional)</label>
                <input style={S.inp} placeholder="Building, floor, flat no." value={form.line2} onChange={e => handleField('line2', e.target.value)} />
              </div>
              {/* Area */}
              <div style={S.fg}>
                <label style={S.fl}>Area / Locality</label>
                <input style={S.inp} placeholder="Auto-filled from map" value={form.location} onChange={e => handleField('location', e.target.value)} />
              </div>
              {/* City + Pincode */}
              <div style={S.row}>
                <div style={{ ...S.fg, flex: 1 }}>
                  <label style={S.fl}>City *</label>
                  <input style={{ ...S.inp, ...(fieldErrors.city ? S.inpErr : {}) }} placeholder="City" value={form.city} onChange={e => handleField('city', e.target.value)} />
                  {fieldErrors.city && <div style={S.fe}>{fieldErrors.city}</div>}
                </div>
                <div style={{ ...S.fg, flex: 1 }}>
                  <label style={S.fl}>Pincode *</label>
                  <input style={{ ...S.inp, ...(fieldErrors.pincode ? S.inpErr : {}) }} inputMode="numeric" placeholder="6-digit" maxLength={6} value={form.pincode} onChange={e => handleField('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} />
                  {fieldErrors.pincode && <div style={S.fe}>{fieldErrors.pincode}</div>}
                </div>
              </div>
              {fieldErrors.coords && <div style={S.errBox}>{fieldErrors.coords}</div>}
              {saveError && <div style={S.errBox}>{saveError}</div>}
            </div>

            <div style={S.saveWrap}>
              <button style={{ ...S.saveBtn, ...(!canSave || isSaving ? S.saveBtnOff : {}) }} onClick={handleSave} disabled={!canSave || isSaving}>
                {isSaving ? 'Saving…' : '📍 Save Address'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

// ── STYLES ─────────────────────────────────────────────────────────────────────
const S = {
  root: { position: 'fixed', inset: 0, zIndex: 10000, background: '#fff' },

  // ── Map phase layout ──
  mapWrap: { flexDirection: 'column', width: '100%', height: '100%' },
  header: {
    background: '#ffffff',
    padding: '12px 16px',
    paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))',
    paddingBottom: 0,
    flexShrink: 0,
    position: 'relative',
    zIndex: 30,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  topBar: {
    display: 'flex', alignItems: 'center', gap: 12,
  },
  backBtn: {
    background: 'none', border: 'none',
    fontSize: 22, cursor: 'pointer', padding: '4px 0',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#1f2937', fontWeight: 300,
  },
  headerTitle: {
    fontSize: 17, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px',
  },
  searchRow: {
    display: 'flex', alignItems: 'center',
    background: '#f9fafb', borderRadius: 12, padding: '0 14px', height: 48,
    border: '1px solid #e5e7eb',
    marginBottom: 12,
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 15,
    color: '#1f2937', background: 'transparent', minWidth: 0,
    fontWeight: 400, fontFamily: 'Inter, -apple-system, sans-serif',
    marginLeft: 10,
  },
  searchAction: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#9ca3af', fontSize: 16, order: -1,
  },

  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: '#fff', maxHeight: 260, overflowY: 'auto', zIndex: 40,
    borderBottom: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  sugItem: {
    display: 'flex', alignItems: 'center', gap: 12,
    width: '100%', padding: '13px 16px', background: 'none', border: 'none',
    borderBottom: '1px solid #f1f5f9', cursor: 'pointer', textAlign: 'left',
  },
  sugPin: { fontSize: 16, flexShrink: 0 },
  sugTextWrap: { flex: 1, minWidth: 0 },
  sugMain: { fontSize: 14, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sugSub: { fontSize: 12, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  // Map area
  mapArea: { flex: 1, position: 'relative', minHeight: 0 },
  mapCanvas: { width: '100%', height: '100%' },

  // Fixed pin
  pin: {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -100%)',
    pointerEvents: 'none', zIndex: 10,
  },

  // Locate me
  locateBtn: {
    position: 'absolute', right: 14, top: 14,
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 24,
    padding: '8px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, zIndex: 20,
  },
  locateText: { fontSize: 13, fontWeight: 600, color: '#374151' },
  spinner: {
    width: 18, height: 18, border: '2.5px solid #e2e8f0',
    borderTopColor: '#ff5a1f', borderRadius: '50%', animation: 'spin 0.7s linear infinite',
  },

  locError: {
    position: 'absolute', bottom: 14, left: 12, right: 12,
    background: 'rgba(254,242,242,0.95)', border: '1px solid #fecaca',
    borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', zIndex: 25,
  },
  mapsErr: {
    position: 'absolute', top: 12, left: 12, right: 12,
    background: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', zIndex: 25,
  },

  // Bottom card
  bottomCard: {
    background: '#fff', padding: '16px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
    borderRadius: '16px 16px 0 0', flexShrink: 0,
    boxShadow: '0 -2px 16px rgba(0,0,0,0.08)',
  },
  cardTitle: { fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 },
  cardSubtitle: { display: 'none' },
  cardAddress: {
    fontSize: 17, fontWeight: 700, color: '#111827', lineHeight: 1.4,
    marginBottom: 4,
    overflow: 'hidden', textOverflow: 'ellipsis',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
    padding: 0, background: 'transparent', borderRadius: 0, border: 'none',
  },
  cardSubAddress: {
    fontSize: 14, fontWeight: 400, color: '#6b7280', lineHeight: 1.4,
    marginBottom: 0,
  },
  continueBtn: {
    width: '100%', background: '#ff5a1f', color: '#fff',
    border: 'none', borderRadius: 14, padding: '16px',
    fontSize: 16, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(255,90,31,0.25)',
    marginTop: 16,
  },
  continueBtnDisabled: { background: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed', boxShadow: 'none' },

  // ── Form phase ──
  formOverlay: {
    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
    justifyContent: 'flex-end', zIndex: 30, background: 'rgba(0,0,0,0.4)',
  },
  formSheet: {
    background: '#fff', borderRadius: '24px 24px 0 0', maxHeight: '85dvh',
    display: 'flex', flexDirection: 'column',
    animation: 'slideUp 0.3s cubic-bezier(0.32,0.72,0,1)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    overflow: 'hidden',
  },
  formHandle: { width: 40, height: 4, borderRadius: 2, background: '#e2e8f0', margin: '12px auto 0', flexShrink: 0 },
  formHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderBottom: '1px solid #f1f5f9', flexShrink: 0,
  },
  formBack: {
    width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9',
    border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: '#374151',
  },
  formTitle: { fontSize: 16, fontWeight: 700, color: '#1e293b' },

  svcYes: { margin: '10px 16px 0', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#065f46', flexShrink: 0 },
  svcNo: { margin: '10px 16px 0', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#991b1b', flexShrink: 0 },

  formBody: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '14px 16px 14px', display: 'flex', flexDirection: 'column', gap: 12, overscrollBehavior: 'contain' },
  fg: { display: 'flex', flexDirection: 'column', gap: 4 },
  fl: { fontSize: 12, fontWeight: 600, color: '#475569' },
  inp: { border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '11px 12px', fontSize: 15, color: '#1e293b', background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box', WebkitAppearance: 'none', fontFamily: 'Inter, -apple-system, sans-serif' },
  inpErr: { borderColor: '#ef4444' },
  fe: { fontSize: 12, color: '#dc2626', marginTop: 2 },
  labelRow: { display: 'flex', gap: 8 },
  chip: { flex: 1, padding: '9px 6px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' },
  chipActive: { background: '#fff4ed', borderColor: '#ff5a1f', color: '#ff5a1f' },
  row: { display: 'flex', gap: 10 },
  errBox: { background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: '#dc2626' },

  saveWrap: { padding: '12px 16px', borderTop: '1px solid #f1f5f9', flexShrink: 0 },
  saveBtn: { width: '100%', background: 'linear-gradient(135deg, #ff5a1f 0%, #e14a12 100%)', color: '#fff', border: 'none', borderRadius: 14, padding: '17px', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(255,90,31,0.3)' },
  saveBtnOff: { background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed', boxShadow: 'none' },
};

export default AddressPicker;
